<template>
  <div class="ds-page">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Wage Payment System</div>
          <h2 class="page-title">WPS Request Items</h2>
          <p class="page-subtitle">Review request items, monitor disbursement progress, and handle approvals by stage.</p>
        </div>
        <div class="col-auto ms-auto">
          <button @click="downloadHandler" class="ds-btn ds-btn--blue mr-2" :disabled="!list.length">
            <font-awesome-icon icon="download" />
            Download
          </button>
          <button @click="goBack" class="ds-btn ds-btn--secondary">
            <font-awesome-icon icon="arrow-left" />
            Back to WPS
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body wps-detail__meta">
        <div class="wps-detail__meta-item">
          <span class="wps-detail__meta-label">Request Number</span>
          <span class="wps-detail__meta-value">{{ requestNumber }}</span>
        </div>
        <div class="wps-detail__meta-item">
          <span class="wps-detail__meta-label">Current Stage</span>
          <span class="wps-detail__meta-value">{{ stageLabel }}</span>
        </div>
        <div class="wps-detail__meta-item">
          <span class="wps-detail__meta-label">Showing</span>
          <span class="wps-detail__meta-value">{{ total_rows }} item{{ total_rows === 1 ? "" : "s" }}</span>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body">
        <div class="wps-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Use the same filter flow as Companies WPS for a consistent review process.</span>
        </div>

        <div class="wps-filter-two-col">
          <div class="wps-filter-left">
            <div class="wps-filter-grid-left">
              <div class="ds-field">
                <label class="ds-field__label">Filter By</label>
                <el-select
                  v-model="filters.filter_by"
                  size="small"
                  class="w-100"
                  placeholder="Select filter"
                  @change="handleFilterByChange"
                >
                  <el-option v-for="item in filterByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Filter Value</label>
                <el-select v-model="filters.filter_value" size="small" class="w-100" placeholder="Select value">
                  <el-option
                    v-for="item in selectedFilterValueOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Sort By</label>
                <el-select v-model="filters.sort_by" size="small" class="w-100" placeholder="Sort by">
                  <el-option v-for="item in sortByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Order</label>
                <el-select v-model="filters.sort_type" size="small" class="w-100" placeholder="Order">
                  <el-option v-for="item in sortTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>

          <div class="wps-filter-sep"></div>

          <div class="wps-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select
                v-model="filters.search_by"
                size="small"
                class="w-100"
                placeholder="Select search"
                @change="handleSearchByChange"
              >
                <el-option v-for="item in searchByOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Value</label>
              <el-input
                v-model="filters.search_value"
                clearable
                size="small"
                :placeholder="searchPlaceholder"
                @clear="searchHandler"
              />
            </div>

            <div class="wps-filter-actions">
              <button @click="resetHandler" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="is_loading">
          <thead>
            <tr>
              <th>Request Date</th>
              <th class="text-right">Base Salary</th>
              <th class="text-right">Allowance</th>
              <th class="text-right">Bonus</th>
              <th class="text-right">Deduction</th>
              <th class="text-right">Gross Amount</th>
              <th class="text-right">Net Amount</th>
              <th>Bank Name</th>
              <th>Account Number</th>
              <th>Account Name</th>
              <th>Status</th>
              <th>Disburse Status</th>
              <th>Reference</th>
              <th>Notes</th>
              <th>Disbursed At</th>
              <th v-if="showActionColumn" class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.request_date_str }}</span>
              </td>
              <td class="text-right">{{ item.base_salary_str }}</td>
              <td class="text-right">{{ item.allowance_str }}</td>
              <td class="text-right">{{ item.bonus_str }}</td>
              <td class="text-right">{{ item.deduction_str }}</td>
              <td class="text-right">
                <span class="ds-amount">{{ item.gross_amount_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ item.net_amount_str }}</span>
              </td>
              <td>{{ item.bank_name || "—" }}</td>
              <td>{{ item.bank_account_number || "—" }}</td>
              <td>{{ item.bank_account_name || "—" }}</td>
              <td>
                <span class="ds-pill" :class="statusPillClass(item.status)">
                  {{ statusLabel(item.status) }}
                </span>
              </td>
              <td>
                <span class="ds-pill" :class="statusPillClass(item.normalized_disbursement_status)">
                  {{ statusLabel(item.normalized_disbursement_status) }}
                </span>
              </td>
              <td>{{ item.disbursement_reference || "—" }}</td>
              <td class="wps-detail__notes">{{ item.disbursement_notes || "—" }}</td>
              <td>{{ item.disbursed_at_str }}</td>
              <td v-if="showActionColumn" class="text-center">
                <div class="ds-row-actions">
                  <el-tooltip v-if="canApproveCurrentStage" content="Approve request item" effect="dark" placement="top">
                    <el-button
                      round
                      size="mini"
                      class="wps-action-approve"
                      type="success"
                      circle
                      @click="handlerApproveBtn(item)"
                    >
                      <font-awesome-icon icon="check" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip
                    :content="canApproveCurrentStage ? 'Reject request item' : 'Rollback request item'"
                    effect="dark"
                    placement="top"
                  >
                    <el-button
                      round
                      size="mini"
                      class="wps-action-reject"
                      type="danger"
                      circle
                      @click="canApproveCurrentStage ? handlerBtnRejectApproval(item) : handlerBtnReject(item)"
                    >
                      <font-awesome-icon :icon="canApproveCurrentStage ? 'ban' : 'undo-alt'" />
                    </el-button>
                  </el-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <el-empty v-if="!total_rows" description="No WPS request items found" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <b-modal size="md" v-model="show.modal_wps" title="Approve WPS" hide-footer>
      <div class="wps-modal-body">
        <el-descriptions :loading="loading.modal_wps" title="" :column="1" border>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="users" size="sm" /> Total WPS Request </template>
            {{ selectedApprovalSummary?.total_recipient || 0 }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="money-bill-alt" size="sm" /> Total WPS Amount </template>
            <strong>{{ formatCurrency(selectedApprovalSummary?.total_amount) }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="hand-holding-usd" size="sm" /> Total Admin Fee </template>
            <strong>{{ formatCurrency(selectedApprovalSummary?.total_admin_fee) }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="money-bill-wave" size="sm" /> Total Disburse Amount </template>
            <strong>{{
              formatCurrency((selectedApprovalSummary?.total_amount || 0) - (selectedApprovalSummary?.total_admin_fee || 0))
            }}</strong>
          </el-descriptions-item>
        </el-descriptions>

        <div class="wps-modal-actions">
          <button @click="show.modal_wps = false" class="ds-btn ds-btn--secondary ds-btn--sm">Cancel</button>
          <button @click="submitApproval" class="ds-btn ds-btn--primary ds-btn--sm">
            <font-awesome-icon icon="check" />
            Submit
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import {
  ROLLBACK_APPROVAL as ROLLBACK_WPS_APPROVAL,
  INQUIRY_APPROVAL as INQUIRY_WPS_APPROVAL,
  SUBMIT_APPROVAL as SUBMIT_WPS_APPROVAL,
} from "@/store/modules/ewa";
import { GET_DETAIL_WPS } from "@/store/modules/wps";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import Pagination from "@/ui/components/Pagination.vue";
import popupErrorMessages from "@/library/popup-error-messages";

const DEFAULT_FILTER_VALUE = "";
const STATUS_TAB_SLUG_MAP = {
  WAITING_APPROVAL: "waiting-approval-finance",
  WAITING_DISBURSEMENT: "waiting-disbursement",
  PROCESS_DISBURSEMENT: "processing-disbursement",
  COMPLETED: "completed",
};

export default {
  name: "DetailWPSCompany",
  components: { Pagination },
  metaInfo: {
    title: "Wage Payment System Detail",
  },
  computed: {
    requestNumber() {
      return this.$route.query.request_number || `#${this.$route.params.id}`;
    },
    statusContext() {
      return this.$route.params.status || "";
    },
    stageLabel() {
      return this.statusLabel(this.statusContext) || "All Stages";
    },
    showActionColumn() {
      return Boolean(this.statusContext && this.statusContext !== "COMPLETED");
    },
    canApproveCurrentStage() {
      return this.statusContext === "WAITING_APPROVAL";
    },
    selectedFilterValueOptions() {
      return this.filters.filter_by === "disbursement_status" ? this.disbursementStatusOptions : this.statusOptions;
    },
    searchPlaceholder() {
      const placeholderMap = {
        bank_account_name: "Input bank account name",
        bank_account_number: "Input bank account number",
        bank_name: "Input bank name",
      };
      return placeholderMap[this.filters.search_by] || "Input search value";
    },
  },
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      page: 1,
      per_page: 10,
      total_rows: 0,
      list: [],
      show: {
        modal_wps: false,
      },
      loading: {
        modal_wps: false,
      },
      selectedApprovalRequest: {},
      selectedApprovalSummary: {},
      filters: {
        filter_by: "status",
        filter_value: DEFAULT_FILTER_VALUE,
        search_by: "bank_account_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      },
      filterByOptions: [
        { value: "status", label: "Status" },
        { value: "disbursement_status", label: "Disburse Status" },
      ],
      statusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL", label: "Waiting Approval" },
        { value: "APPROVED", label: "Approved" },
        { value: "REJECTED", label: "Rejected" },
      ],
      disbursementStatusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL", label: "Waiting Approval" },
        { value: "WAITING_DISBURSEMENT", label: "Waiting Disbursement" },
        { value: "PROCESS_DISBURSEMENT", label: "Processing Disbursement" },
        { value: "COMPLETED", label: "Completed" },
        { value: "FAILED", label: "Failed" },
      ],
      searchByOptions: [
        { value: "bank_account_name", label: "Bank Account Name" },
        { value: "bank_account_number", label: "Bank Account Number" },
        { value: "bank_name", label: "Bank Name" },
      ],
      sortByOptions: [
        { value: "created_at", label: "Request Date" },
        { value: "bank_account_name", label: "Bank Account Name" },
        { value: "bank_account_number", label: "Bank Account Number" },
        { value: "bank_name", label: "Bank Name" },
        { value: "disbursed_at", label: "Disbursed At" },
      ],
      sortTypeOptions: [
        { value: "desc", label: "Descending" },
        { value: "asc", label: "Ascending" },
      ],
    };
  },
  async mounted() {
    await this.getList();
  },
  methods: {
    getApiNamespace() {
      return this.$route.path.startsWith("/wage-payment-system") ? "wage-payment-system" : "wps";
    },
    goBack() {
      this.$router.push({
        name: "Wage Payment System",
        params: {
          tab: STATUS_TAB_SLUG_MAP[this.statusContext] || STATUS_TAB_SLUG_MAP.WAITING_APPROVAL,
        },
      });
    },
    formatCurrency(value) {
      return `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    statusPillClass(status) {
      const value = String(status || "").toUpperCase();
      switch (value) {
        case "APPROVED":
        case "COMPLETED":
          return "ds-pill--success";
        case "REJECTED":
        case "FAILED":
          return "ds-pill--danger";
        case "PROCESS_DISBURSEMENT":
        case "PROCESSING":
          return "ds-pill--info";
        case "WAITING_APPROVAL":
        case "WAITING_DISBURSEMENT":
        case "WAITING_PAYMENT":
          return "ds-pill--warning";
        default:
          return "ds-pill--gray";
      }
    },
    statusLabel(status) {
      if (!status || status === "—") return "—";
      return String(status)
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    mapItem(item) {
      const normalizedDisbursementStatus = item.disbursement_status || item.disburse_status || "";

      return {
        ...item,
        request_date_str: item.created_at ? moment(item.created_at).format("DD MMM YYYY, HH:mm") : "—",
        base_salary_str: this.formatCurrency(item.base_salary),
        allowance_str: this.formatCurrency(item.allowance),
        bonus_str: this.formatCurrency(item.bonus),
        deduction_str: this.formatCurrency(item.deduction),
        gross_amount_str: this.formatCurrency(item.gross_amount),
        net_amount_str: this.formatCurrency(item.net_amount),
        disbursed_at_str: item.disbursed_at ? moment(item.disbursed_at).format("DD MMM YYYY, HH:mm") : "—",
        normalized_disbursement_status: normalizedDisbursementStatus || "—",
      };
    },
    buildQuery(extraParams = {}) {
      const query = {
        page: this.page,
        size: this.per_page,
        per_page: this.per_page,
        sort: this.filters.sort_type === "desc" ? `-${this.filters.sort_by}` : this.filters.sort_by,
        filter_date: "created_at",
        ...extraParams,
      };

      if (this.filters.filter_by && this.filters.filter_value) {
        query[this.filters.filter_by] = this.filters.filter_value;
      }

      if (this.filters.search_by && this.filters.search_value) {
        query.search = this.filters.search_by;
        query.search_value = this.filters.search_value;
      }

      return query;
    },
    handleFilterByChange() {
      this.filters.filter_value = "";
    },
    handleSearchByChange() {
      this.filters.search_value = "";
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    resetHandler() {
      this.filters = {
        filter_by: "status",
        filter_value: DEFAULT_FILTER_VALUE,
        search_by: "bank_account_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      };
      this.page = 1;
      this.getList();
    },
    async downloadHandler() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const response = await this.$store.dispatch(GET_DETAIL_WPS, {
          id: this.$route.params.id,
          api_namespace: this.getApiNamespace(),
          query: this.buildQuery({
            action: "download",
          }),
        });
        const data = response?.data ?? response;
        const fileName = `${String(this.requestNumber || "wps-request").replace(/[^\w.-]+/g, "_")}-items.csv`;

        this.$message({
          title: "Success",
          type: "success",
          message: "Success! Your CSV download will start shortly.",
        });

        this.$nextTick(() => {
          const anchor = document.createElement("a");
          anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
          anchor.target = "_blank";
          anchor.download = fileName;
          anchor.click();
        });
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    async pageChangeHandler(page) {
      this.page = page;
      await this.getList();
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const data = await this.$store.dispatch(GET_DETAIL_WPS, {
          id: this.$route.params.id,
          api_namespace: this.getApiNamespace(),
          query: this.buildQuery(),
        });
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        this.list = rows.map((item) => this.mapItem(item));
        this.total_rows = data?.count || 0;
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    handlerApproveBtn(item) {
      this.show.modal_wps = true;
      this.selectedApprovalRequest = {
        ...item,
        current_state: this.statusContext,
      };
      this.inquiry();
    },
    async inquiry() {
      this.loading.modal_wps = true;
      try {
        const response = await this.$store.dispatch(INQUIRY_WPS_APPROVAL, {
          id: this.selectedApprovalRequest.company_id,
          query: {
            current_state: this.selectedApprovalRequest.current_state,
            owned_by: this.selectedApprovalRequest.owned_by?.toString(),
            approval_type: "single",
            bank_code: null,
          },
        });
        this.selectedApprovalRequest.approval_id = response.approval_id;
        this.selectedApprovalSummary = response;
      } finally {
        this.loading.modal_wps = false;
      }
    },
    async submitApproval() {
      this.$confirm("You will approve this WPS disbursement. You can not undo this action. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            await this.$store
              .dispatch(SUBMIT_WPS_APPROVAL, {
                id: this.selectedApprovalRequest.company_id,
                data: {
                  approval_id: this.selectedApprovalRequest.approval_id,
                  bank_code: null,
                },
              })
              .then(() => {
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Approval success",
                });
                this.show.modal_wps = false;
                this.getList();
              })
              .catch((err) => {
                if (err.response?.data) popupErrorMessages(err.response.data);
              });
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    handlerBtnReject(item) {
      this.$confirm("You rollback this request. You can not undo this action. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            await this.$store
              .dispatch(ROLLBACK_WPS_APPROVAL, {
                id: this.$route.params.id,
                current_state: this.statusContext,
                data: {
                  owned_by: item.owned_by,
                },
              })
              .then(() => {
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Rollback success",
                });
                this.getList();
              })
              .catch((err) => {
                if (err.response?.data) popupErrorMessages(err.response.data);
              });
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    handlerBtnRejectApproval(item) {
      this.$confirm("You reject this request. You can not undo this action. Continue?", "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            await this.$store
              .dispatch(ROLLBACK_WPS_APPROVAL, {
                id: this.$route.params.id,
                current_state: this.statusContext,
                data: {
                  owned_by: item.owned_by,
                },
              })
              .then(() => {
                this.$message({
                  title: "Success",
                  type: "success",
                  message: "Reject success",
                });
                this.getList();
              })
              .catch((err) => {
                if (err.response?.data) popupErrorMessages(err.response.data);
              });
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.wps-detail__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: center;
}

.wps-detail__meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wps-detail__meta-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ds-text-hint);
}

.wps-detail__meta-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-base);
}

.wps-detail__notes {
  max-width: 220px;
  white-space: normal;
  line-height: 1.4;
}

.wps-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.wps-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}

.wps-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}

.wps-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}

.wps-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wps-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

.wps-action-approve.el-button,
.wps-action-reject.el-button {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  font-size: 11px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.wps-action-approve.el-button--success {
  background: rgba(47, 179, 68, 0.1) !important;
  border-color: rgba(47, 179, 68, 0.24) !important;
  color: #2fb344 !important;
}

.wps-action-approve.el-button--success:hover {
  background: rgba(47, 179, 68, 0.2) !important;
}

.wps-action-reject.el-button--danger {
  background: rgba(214, 57, 57, 0.1) !important;
  border-color: rgba(214, 57, 57, 0.24) !important;
  color: #d63939 !important;
}

.wps-action-reject.el-button--danger:hover {
  background: rgba(214, 57, 57, 0.2) !important;
}

.wps-modal-body {
  padding: 4px 0;
}

.wps-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

@media (max-width: 991px) {
  .wps-detail__meta {
    grid-template-columns: 1fr;
  }

  .wps-filter-two-col {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .wps-filter-sep {
    display: none;
  }

  .wps-filter-grid-left {
    grid-template-columns: 1fr;
  }
}
</style>
